import { SyntheticEvent, useCallback, useMemo } from 'react';
import cls from 'classnames';
import PlayFill from 'icon/play_fill.svg';
import Heart from 'icon/heart.svg';
import _clamp from 'lodash/clamp';
import { Article } from 'shared/models/legacy';
import { useNumFormatter } from '~/utils/format';
import { useViewTracking } from '~/utils/use-view-tracking';
import { Link, useLocation, useNavigate } from '@remix-run/react';
import { ClickPosition } from '~/utils/click-position';
import { ModuleType } from '~/utils/module-type';
import { ExtractAtomValue, useAtomValue } from 'jotai';
import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';
import { $CurrentPageModule } from '~/models/current-page-module';
import useReflowClick from '~/utils/use-reflow-click';
import { usePageActive } from '~/utils/active/use-page-active';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { DETAIL_POST_MODAL_NAME, EArticleClass, LegacyArticleUtil } from 'shared/models';
import { $InstalledRelatedApp } from '@lemon8/web-app-shared/atom';
import { useSEOHref } from '~/utils/build-seo-url';

import './style.scss';

interface ArticleRecommendCardProps {
  article: Article;
  rank: number;
}

/**
 * ArticleCard for ExperiencePage with Clickable Post Detail
 * @param props
 * @returns
 */
export default function ArticleRecommendCardV3(props: ArticleRecommendCardProps): JSX.Element {
  const formatNum = useNumFormatter();

  const { article, rank } = props;

  const articleLink = useSEOHref(LegacyArticleUtil.getArticlePath(article));

  const authorLink = useSEOHref(LegacyArticleUtil.getAuthorPath(article));

  const { imageList, articleClass, title, groupId, largeImage, author, articleLikes, imprId } = article;

  const isMobileUser = useAtomValue($IsMobileUser);

  const isOldUser = !(['pending', null] as ExtractAtomValue<typeof $InstalledRelatedApp>[]).includes(
    useAtomValue($InstalledRelatedApp),
  );

  const prefix = useScopedAtomValue($CurrentPositionPrefix);

  const currentPageModule = useScopedAtomValue($CurrentPageModule);

  const active = usePageActive();
  const navigate = useNavigate();
  const location = useLocation();

  const cardObservedRef = useViewTracking<HTMLAnchorElement>({
    onView: useCallback(() => {
      window.Tea('wap_related_post_show', {
        current_page_module: currentPageModule,
        current_position: prefix + ClickPosition.related_post,
        group_id: groupId,
        media_id: author.userId,
        article_class: articleClass,
        impr_id: imprId,
        rank,
      });
    }, [isMobileUser]),
  });

  const handleClickArticle = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_page_module: currentPageModule,
        current_position: prefix + ClickPosition.related_post,
        page_click_id: groupId,
        rank,
      }),
      finallyBehavior: (event) => {
        event?.preventDefault();
        if (!isOldUser) {
          navigate(
            {
              pathname: '.',
              search: location.search, // must keep search otherwise search will disappear
            },
            {
              state: { enterName: DETAIL_POST_MODAL_NAME, gid: groupId, authorId: author.userId },
            },
          ); // push state but not change url
        } else {
          active({
            schemaOption: {
              pageType: articleClass === EArticleClass.VIDEO ? 'video' : 'article',
              groupId,
              authorId: author.userId,
            },
            ampExtra: {
              enter_position: prefix + ClickPosition.related_post,
            },
          });
        }
      },
    },
    [active, groupId, articleClass, author],
  );

  const aspectRatio = useMemo(() => {
    switch (articleClass) {
      case EArticleClass.GALLERY:
        return _clamp(imageList[0].width / imageList[0].height, 3 / 4, 4 / 3);
      case EArticleClass.ARTICLE:
      case EArticleClass.VIDEO:
      default:
        return _clamp(largeImage.width / largeImage.height, 3 / 4, 4 / 3);
    }
  }, [article]);

  const coverUrl = useMemo(() => {
    switch (article.articleClass) {
      case EArticleClass.GALLERY:
        return article.imageList[0].url;
      case EArticleClass.ARTICLE:
      case EArticleClass.VIDEO:
      default:
        return article.largeImage.url;
    }
  }, [article]);

  return (
    <Link to={articleLink} className="article-recommend-card" onClick={handleClickArticle} ref={cardObservedRef}>
      <div
        className={cls('article-cover', {
          video: article.articleClass === EArticleClass.VIDEO,
        })}
        style={{ paddingBottom: `${(1 / aspectRatio) * 100}%` }}
      >
        <img loading="lazy" src={coverUrl} onContextMenu={(e: SyntheticEvent) => e.preventDefault()} />
        {article.articleClass === EArticleClass.VIDEO && <PlayFill className="play-icon" />}
      </div>
      <div className="article-content">
        <div className="article-title">{title}</div>
        <object>
          <Link to={authorLink} className="article-desc" onClick={(e: SyntheticEvent) => e.preventDefault()}>
            <img
              loading="lazy"
              className="author-avatar"
              src={article.author.avatar}
              onContextMenu={(e: SyntheticEvent) => e.preventDefault()}
            />
            <span className="author-name">{article.author.nickName}</span>
            <div className="digg">
              <Heart />
              {Boolean(articleLikes) && <span>{formatNum(articleLikes)}</span>}
            </div>
          </Link>
        </object>
      </div>
    </Link>
  );
}
